import React, { useState } from 'react';
import { Star, MapPin, Heart } from 'lucide-react';
import styles from './HotelDetailsCard.module.scss';
import { HOTEL_SEED, type HotelSeedItem } from '../data/hotelSeed';
import { Modal } from './Modal';
import { HotelDetailsExpanded } from './HotelDetailsExpanded';

export const MOCK_HOTELS = HOTEL_SEED;

export interface HotelDetail {
    id: string;
    name: string;
    location: string;
    stars: number;
    rating: number;
    ratingLabel: string;
    pricePerNight: number;
    totalPrice: number;
    currency?: string;
    price?: string;
    nights: number;
    image: string;
    images?: string[];
    rooms?: any[];
    roomsTotal?: number;
    priceBands?: Array<{ band: number; min: number; max: number }>;
    amenities: string[];
    description: string;
    category: string;
}

interface HotelDetailsCardProps {
    hotel?: HotelDetail | HotelSeedItem;
    onBook?: (hotel: HotelDetail) => void;
    onViewRooms?: (hotel: HotelDetail) => void;
    useMock?: boolean;
}

const toNumberPrice = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number(String(value || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeHotelForDetail = (hotel: HotelDetail | HotelSeedItem): HotelDetail => {
    const nights = (hotel as HotelDetail).nights || (hotel as HotelSeedItem).nights || 4;
    const nightly = (hotel as HotelDetail).pricePerNight || (hotel as HotelSeedItem).pricePerNight || toNumberPrice((hotel as HotelSeedItem).price);
    const total = (hotel as HotelDetail).totalPrice || (hotel as HotelSeedItem).totalPrice || Math.round(nightly * nights);
    const rating = (hotel as HotelDetail).rating || (hotel as HotelSeedItem).rating || Math.min(9.9, 7.8 + ((hotel.stars || 0) * 0.3));
    const ratingLabel = (hotel as HotelDetail).ratingLabel || (hotel as HotelSeedItem).ratingLabel || (rating >= 9 ? 'Exceptional' : rating >= 8 ? 'Excellent' : 'Very Good');
    const amenities = (hotel as HotelDetail).amenities || (hotel as HotelSeedItem).amenities || [
        'Free WiFi',
        (hotel as HotelSeedItem).breakfastIncluded ? 'Breakfast' : 'Bar',
        'Parking',
    ];
    return {
        id: hotel.id,
        name: hotel.name,
        location: (hotel as HotelDetail).location || (hotel as HotelSeedItem).location || 'City Center',
        stars: hotel.stars,
        rating: Number(rating.toFixed(1)),
        ratingLabel,
        pricePerNight: nightly,
        totalPrice: total,
        currency: (hotel as HotelDetail).currency || (hotel as HotelSeedItem).currency,
        price: (hotel as HotelDetail).price || (hotel as HotelSeedItem).price,
        nights,
        image: hotel.image,
        images: (hotel as HotelDetail).images || (hotel as HotelSeedItem).images,
        rooms: (hotel as HotelDetail).rooms || (hotel as HotelSeedItem).rooms,
        roomsTotal: (hotel as HotelDetail).roomsTotal || (hotel as HotelSeedItem).roomsTotal,
        priceBands: (hotel as HotelDetail).priceBands || (hotel as HotelSeedItem).priceBands,
        amenities,
        description: (hotel as HotelDetail).description || (hotel as HotelSeedItem).description || `${hotel.name} with ${hotel.stars}-star comfort and curated amenities.`,
        category: (hotel as HotelDetail).category || (hotel as HotelSeedItem).category || (hotel.stars >= 5 ? 'Luxury' : 'Premium'),
    };
};

export const HotelDetailsCard: React.FC<HotelDetailsCardProps> = ({ hotel, onBook, onViewRooms, useMock = false }) => {
    const raw = hotel || (useMock ? MOCK_HOTELS[0] : null);
    const data = raw ? normalizeHotelForDetail(raw) : null;
    const [saved, setSaved] = useState(false);
    const [open, setOpen] = useState(false);

    if (!data) return null;

    return (
        <>
        <div
            className={styles.card}
            role="button"
            tabIndex={0}
            onClick={() => setOpen(true)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpen(true);
                }
            }}
        >
            <div className={styles.media}>
                <img src={data.image} alt={data.name} loading="lazy" />
                <div className={styles.mediaOverlay} />
                <span className={styles.badge}>{data.category}</span>
                <button
                    className={`${styles.heart} ${saved ? styles.heartSaved : ''}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setSaved((s) => !s);
                    }}
                    aria-label="Save hotel"
                >
                    <Heart size={16} fill={saved ? 'currentColor' : 'none'} />
                </button>
            </div>

            <div className={styles.body}>
                <div className={styles.header}>
                    <h3 className={styles.name}>{data.name}</h3>
                    <div className={styles.location}>
                        <MapPin size={13} />
                        <span>{data.location}</span>
                    </div>
                </div>

                <div className={styles.ratingRow}>
                    <div className={styles.stars}>
                        {Array.from({ length: 5 }, (_, i) => (
                            <Star key={i} size={13} fill={i < data.stars ? 'currentColor' : 'none'} strokeWidth={2} />
                        ))}
                    </div>
                    <span className={styles.ratingBadge}>{data.rating}</span>
                    <span className={styles.ratingLabel}>{data.ratingLabel}</span>
                </div>

                <p className={styles.description}>{data.description}</p>

                <div className={styles.amenities}>
                    {data.amenities.map((a, i) => (
                        <span key={i} className={styles.amenity}>{a}</span>
                    ))}
                </div>

                <div className={styles.footer}>
                    <div className={styles.price}>
                        <span className={styles.priceAmount}>{data.price || `${data.currency || ''} ${data.pricePerNight}`.trim()}</span>
                        <span className={styles.pricePer}>/night</span>
                        <span className={styles.priceTotal}>{`${data.currency || ''} ${data.totalPrice.toLocaleString()}`.trim()} total · {data.nights} nights</span>
                    </div>
                    <div className={styles.actions}>
                        {onViewRooms && (
                            <button
                                className={styles.btnSecondary}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onViewRooms(data);
                                }}
                            >
                                View Rooms
                            </button>
                        )}
                        <button
                            className={styles.btnPrimary}
                            onClick={(event) => {
                                event.stopPropagation();
                                onBook?.(data);
                            }}
                        >
                            Book Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <Modal isOpen={open} onClose={() => setOpen(false)} size="pageNarrow">
            <HotelDetailsExpanded
                hotel={data}
                onChooseRoom={(expandedHotel) => {
                    setOpen(false);
                    onViewRooms?.(expandedHotel);
                }}
            />
        </Modal>
        </>
    );
};

interface HotelDetailsCardListProps {
    hotels?: HotelDetail[];
    onBook?: (hotel: HotelDetail) => void;
    onViewRooms?: (hotel: HotelDetail) => void;
}

export const HotelDetailsCardList: React.FC<HotelDetailsCardListProps> = ({ hotels, onBook, onViewRooms }) => {
    const items = hotels && hotels.length > 0 ? hotels : MOCK_HOTELS;
    return (
        <div className={styles.list}>
            {items.map((h) => (
                <HotelDetailsCard key={h.id} hotel={h} onBook={onBook} onViewRooms={onViewRooms} />
            ))}
        </div>
    );
};
